import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Tooltip from 'fe-design-base/molecules/Tooltip';
import PropTypes from 'prop-types';

import UpgradeWrapper from 'features/permissions/UpgradeWrapper';
import ActionButtons from 'features/timesheets/TimesheetsPage/TimecardModal/ActionButtons';
import { editTimecard } from 'features/timesheets/TimesheetsPage/TimecardModal/slices';

import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';

const Header = ({
  canManageTimecards,
  closeModal,
  date,
  employeeId,
  employeeName,
  employeePhone,
  hasTimecardLockPermission,
  hideLockIcon,
  isLockDisabled,
  isMobile,
  locationName,
  lockedMessage,
  onUpdateLockedStatus,
  paywallVersion,
  roleName,
  timesheetApprovalEnabled,
  employeeFirstName,
}) => {
  const dispatch = useDispatch();
  const handleClickEdit = useCallback(() => {
    trackUxEvent({
      actionType: TRACK_ACTION_TYPES.CLICK,
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.EDIT_CLICKED_V3,
    });

    dispatch(editTimecard());
  }, [dispatch]);

  const tooltipLockedContent = () => {
    const enabled_feature_copy = hasTimecardLockPermission
      ? 'locked'
      : 'no_permission_lock';
    const toolTipCopy = `timecard_modal_v3.header.tooltips.${
      timesheetApprovalEnabled ? enabled_feature_copy : 'feature_disabled'
    }`;

    return (
      <Box>
        <Text inline-block fs16 lh24 mb8>
          {lockedMessage}.
        </Text>
        <Text fs16 i18n={toolTipCopy} />
      </Box>
    );
  };

  const tooltipUnlockedContent = () => {
    const enabled_feature_copy = hasTimecardLockPermission
      ? 'unlocked'
      : 'no_permission_lock';
    const toolTipCopy = `timecard_modal_v3.header.tooltips.${
      timesheetApprovalEnabled ? enabled_feature_copy : 'feature_disabled'
    }`;
    return <Text fs16 i18n={toolTipCopy} />;
  };

  const renderTimecardLock = () => {
    if (!!lockedMessage) {
      return (
        <Box w={!isMobile ? 116 : 100} pr={isMobile ? 16 : 24}>
          <UpgradeWrapper
            feature="timesheet_approval"
            paywallVersion={paywallVersion}
            focusedFeature="laborCostManagement"
          >
            <Tooltip position="top" content={tooltipLockedContent()}>
              <Button
                startIcon="Lock"
                variant="secondary"
                onClick={onUpdateLockedStatus}
                disabled={isLockDisabled}
                size="medium"
                dataTestId="ReadMode-Header__lock-icon"
              >
                {toI18n('timecard_modal_v3.header.locked')}
              </Button>
            </Tooltip>
          </UpgradeWrapper>
        </Box>
      );
    }

    return (
      <Box
        row
        w={isMobile ? 130 : 103}
        mr={isMobile ? 16 : 0}
        h={40}
        minw={200}
        hright
      >
        {!hideLockIcon && canManageTimecards && (
          <Box mr={10} grow={0}>
            <UpgradeWrapper
              focusedFeature="none"
              feature="timesheet_approval"
              paywallVersion={paywallVersion}
            >
              <Tooltip position="top" content={tooltipUnlockedContent()}>
                <Button
                  startIcon="Unlock"
                  variant="secondary"
                  onClick={onUpdateLockedStatus}
                  style={{ paddingLeft: 12, paddingRight: 7 }}
                  dataTestId="ReadMode-Header__unlock-icon"
                  disabled={isLockDisabled}
                >
                  {toI18n('timecard_modal_v3.header.lock')}
                </Button>
              </Tooltip>
            </UpgradeWrapper>
          </Box>
        )}
        {canManageTimecards && (
          <Box>
            <Button
              startIcon="Edit"
              variant="primary"
              size="medium"
              onClick={handleClickEdit}
              style={{ paddingLeft: 12, paddingRight: 7 }}
            >
              {toI18n('timecard_modal_v3.header.edit')}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box p={24} pt={16} pb={16}>
        <Box row>
          <Box minw={0} grow={1}>
            <Box>
              <Text color="mono900" textAlign="left" variant="eyebrow">
                {date.format('ddd, MMM D')}
              </Text>
            </Box>
            <Box mt={5} ellipsis>
              <Text variant="heading2" color="mono900">
                <Box ellipsis maxw="90%">
                  {employeeName}
                </Box>
              </Text>
            </Box>
            {roleName && (
              <Box w={590} row vcenter mt={5} pb={!roleName ? 10 : 0}>
                <Text
                  color="mono700"
                  variant="body"
                  i18n="timecard_modal_v3.header.role_worked"
                  i18nProps={{ role: roleName, location: locationName }}
                />
              </Box>
            )}
            {canManageTimecards && (
              <Box mt={5}>
                <ActionButtons
                  isMobile={isMobile}
                  userId={employeeId}
                  userName={employeeName}
                  userFirstName={employeeFirstName}
                  userPhone={employeePhone}
                  closeModal={closeModal}
                />
              </Box>
            )}
          </Box>
          {renderTimecardLock()}
        </Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  canManageTimecards: PropTypes.bool.isRequired,
  date: PropTypes.object.isRequired,
  editMode: PropTypes.bool,
  employeeId: PropTypes.number,
  employeeName: PropTypes.string,
  employeePhone: PropTypes.string,
  isLockDisabled: PropTypes.bool.isRequired,
  locationName: PropTypes.string,
  lockedMessage: PropTypes.string,
  onUpdateLockedStatus: PropTypes.func.isRequired,
  paywallVersion: PropTypes.string,
  roleName: PropTypes.string,
};

export default Header;
